import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Mail, Calendar, Loader2, Send, Eye, Save, Plus, X, ToggleLeft, ToggleRight, CheckCircle2,
} from 'lucide-react';

const NZ_WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/**
 * Compact Dashboard widget that lets the operator toggle / configure /
 * preview / manually-fire the weekly marketing recap email. Backed by
 * /api/marketing-recap/* endpoints + the auto-scheduler in server.py.
 */
export default function MarketingRecapCard() {
  const { api, user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [flash, setFlash] = useState('');
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/marketing-recap/settings');
      setSettings(data);
      setDraft(data);
    } catch { setSettings(null); }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const flashMsg = (msg) => { setFlash(msg); setTimeout(() => setFlash(''), 3000); };

  const save = async () => {
    if (!isAdmin) return;
    setBusy('save');
    try {
      const payload = {
        enabled: !!draft.enabled,
        recipients: (draft.recipients || []).filter(r => r && r.includes('@')),
        weekday: parseInt(draft.weekday ?? 0, 10),
        hour_nz: parseInt(draft.hour_nz ?? 8, 10),
      };
      const { data } = await api.put('/marketing-recap/settings', payload);
      setSettings(data);
      setDraft(data);
      flashMsg('Saved ✓');
    } catch (e) {
      flashMsg(e?.response?.data?.detail || 'Save failed');
    }
    setBusy('');
  };

  const sendNow = async () => {
    if (!isAdmin) return;
    if (!(draft?.recipients || []).length) { flashMsg('Add a recipient first'); return; }
    if (!window.confirm(`Send the weekly marketing recap now to ${(draft.recipients || []).length} recipient(s)?`)) return;
    setBusy('send');
    try {
      const { data } = await api.post('/marketing-recap/send-now');
      // Flash status, then reload settings — keep flash visible AFTER load
      // resolves (was previously racing the 3s timeout when network is slow).
      let msg;
      if (data?.status === 'sent') msg = `Sent to ${data.dispatched_count} recipient(s)`;
      else if (data?.status === 'logged') msg = 'Logged (SMTP not configured)';
      else msg = `Status: ${data?.status || 'unknown'}`;
      await load();
      flashMsg(msg);
    } catch (e) {
      flashMsg(e?.response?.data?.detail || 'Send failed');
    }
    setBusy('');
  };

  const loadPreview = async () => {
    setBusy('preview');
    try {
      const { data } = await api.get('/marketing-recap/preview');
      setPreview(data);
      setShowPreview(true);
    } catch (e) {
      flashMsg(e?.response?.data?.detail || 'Preview failed');
    }
    setBusy('');
  };

  const addRecipient = () => {
    if (!newEmail || !newEmail.includes('@')) return;
    const next = [...(draft.recipients || []), newEmail.trim()];
    setDraft({ ...draft, recipients: Array.from(new Set(next)) });
    setNewEmail('');
  };

  const removeRecipient = (i) => {
    const next = (draft.recipients || []).filter((_, idx) => idx !== i);
    setDraft({ ...draft, recipients: next });
  };

  if (loading) {
    return <div data-testid="recap-loading" className="bg-white border border-slate-200 rounded-xl p-4 text-xs text-slate-400 flex items-center gap-2"><Loader2 className="animate-spin" size={12} /> Loading recap settings…</div>;
  }
  if (!settings || !draft) return null;

  const dirty = JSON.stringify(settings) !== JSON.stringify(draft);
  const lastSent = settings.last_sent_at ? new Date(settings.last_sent_at) : null;

  return (
    <section data-testid="marketing-recap-card" className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <header className="px-4 py-2.5 border-b border-slate-100 bg-gradient-to-r from-orange-50 to-amber-50 flex items-center gap-2">
        <Mail size={13} className="text-[#D35400]" />
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">Weekly Marketing Recap</h3>
        <span className="text-[10px] text-slate-500">
          Auto-emailed every {NZ_WEEKDAYS[settings.weekday || 0]} @ {String(settings.hour_nz ?? 8).padStart(2, '0')}:00 NZ
        </span>
        <span className="ml-auto flex items-center gap-2">
          {flash && <span className="text-[10px] text-emerald-700 font-semibold flex items-center gap-1"><CheckCircle2 size={10} /> {flash}</span>}
        </span>
      </header>

      <div className="p-4 space-y-3">
        {/* Enabled toggle + last-sent */}
        <div className="flex items-center gap-3 flex-wrap">
          <button
            data-testid="recap-enabled-toggle"
            type="button"
            disabled={!isAdmin}
            onClick={() => isAdmin && setDraft({ ...draft, enabled: !draft.enabled })}
            className="flex items-center gap-2 text-sm font-semibold"
          >
            {draft.enabled ? <ToggleRight size={28} className="text-emerald-600" /> : <ToggleLeft size={28} className="text-slate-400" />}
            <span className={draft.enabled ? 'text-emerald-700' : 'text-slate-500'}>
              {draft.enabled ? 'Scheduled recap is ON' : 'Scheduled recap is OFF'}
            </span>
          </button>
          <span className="text-[11px] text-slate-400 ml-auto">
            {lastSent
              ? <>Last sent {lastSent.toLocaleString('en-NZ')} · <span className={settings.last_run_status === 'sent' ? 'text-emerald-700' : 'text-amber-700'}>{settings.last_run_status}</span></>
              : 'Not sent yet'}
          </span>
        </div>

        {/* Schedule */}
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <Calendar size={12} className="text-slate-400" />
          <span className="text-slate-500">Send on</span>
          <select
            data-testid="recap-weekday"
            disabled={!isAdmin}
            value={draft.weekday ?? 0}
            onChange={e => setDraft({ ...draft, weekday: parseInt(e.target.value, 10) })}
            className="px-2 py-1 border border-slate-200 rounded text-xs"
          >
            {NZ_WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
          </select>
          <span className="text-slate-500">at</span>
          <select
            data-testid="recap-hour"
            disabled={!isAdmin}
            value={draft.hour_nz ?? 8}
            onChange={e => setDraft({ ...draft, hour_nz: parseInt(e.target.value, 10) })}
            className="px-2 py-1 border border-slate-200 rounded text-xs"
          >
            {Array.from({ length: 24 }).map((_, h) => (
              <option key={h} value={h}>{String(h).padStart(2, '0')}:00 NZ</option>
            ))}
          </select>
        </div>

        {/* Recipients */}
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Recipients</label>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {(draft.recipients || []).map((r, i) => (
              <span key={r} data-testid={`recap-recipient-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-700 text-xs rounded-full">
                <Mail size={10} />
                {r}
                {isAdmin && (
                  <button data-testid={`recap-recipient-remove-${i}`} onClick={() => removeRecipient(i)} className="ml-1 text-slate-400 hover:text-red-500">
                    <X size={10} />
                  </button>
                )}
              </span>
            ))}
            {(draft.recipients || []).length === 0 && <span className="text-xs text-slate-300 italic">No recipients — add one below ↓</span>}
          </div>
          {isAdmin && (
            <div className="flex gap-2">
              <input
                data-testid="recap-new-recipient"
                type="email"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addRecipient(); } }}
                placeholder="someone@rtr.co.nz"
                className="flex-1 px-3 py-1.5 border border-slate-200 rounded-md text-xs focus:border-[#D35400] focus:outline-none"
              />
              <button data-testid="recap-add-recipient" type="button" onClick={addRecipient} disabled={!newEmail.includes('@')} className="flex items-center gap-1 px-3 py-1.5 bg-[#D35400] text-white text-xs font-semibold rounded-md disabled:opacity-40 hover:bg-[#A04000]">
                <Plus size={11} /> Add
              </button>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 pt-1 flex-wrap">
          <button
            data-testid="recap-preview-btn"
            type="button"
            onClick={loadPreview}
            disabled={busy === 'preview'}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 text-slate-700 text-xs font-semibold rounded-md hover:bg-slate-50 disabled:opacity-40"
          >
            {busy === 'preview' ? <Loader2 size={11} className="animate-spin" /> : <Eye size={11} />}
            Preview
          </button>
          {isAdmin && (
            <>
              <button
                data-testid="recap-save-btn"
                type="button"
                onClick={save}
                disabled={!dirty || busy === 'save'}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-[#D35400] text-[#D35400] text-xs font-semibold rounded-md hover:bg-orange-50 disabled:opacity-40"
              >
                {busy === 'save' ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                Save changes{dirty ? ' •' : ''}
              </button>
              <button
                data-testid="recap-send-now-btn"
                type="button"
                onClick={sendNow}
                disabled={busy === 'send' || (draft.recipients || []).length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-md hover:bg-emerald-700 disabled:opacity-40"
              >
                {busy === 'send' ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
                Send now
              </button>
            </>
          )}
        </div>
        {!isAdmin && <p className="text-[10px] text-slate-400 italic">Read-only — only admin/owner can change recap settings.</p>}
      </div>

      {/* Preview modal */}
      {showPreview && preview && (
        <div data-testid="recap-preview-modal" className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowPreview(false)}>
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div>
                <h3 className="text-sm font-bold text-slate-800">Recap preview</h3>
                <p className="text-[11px] text-slate-500">{preview.data?.date_from} → {preview.data?.date_to}</p>
              </div>
              <button onClick={() => setShowPreview(false)} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
            </header>
            <iframe
              data-testid="recap-preview-iframe"
              srcDoc={preview.html}
              sandbox=""
              title="Marketing recap preview"
              className="flex-1 w-full bg-slate-50 border-0"
            />
          </div>
        </div>
      )}
    </section>
  );
}
